/* eslint-disable max-len, no-restricted-properties, padded-blocks, prefer-destructuring */
import { mapState, mapGetters } from 'vuex';
import FiscalYear from '@/mixins/fiscalYear';

export default {
  mixins: [
    FiscalYear,
  ],
  computed: {
    ...mapGetters('Project', [
      'fundingSourceMap',
      'paidWorkorderBillLineItems',
    ]),
    ...mapState('Project', {
      projectIncentivePayments: (state) => state.projectIncentivePayments,
    }),
    showProjectExpenditureSummary() {
      if (!this.config || this.config === null) return false;
      if (!this.project || this.project === null) return false;
      const approvalStatus = [
        this.config.projectOverviewPage.submitStatus.Approved,
        this.config.projectOverviewPage.submitStatus.Completed,
        this.config.projectOverviewPage.submitStatus.Cancelled,
      ];
      return approvalStatus.includes(this.project.approvalStatus);
    },
    showProjectBudgetSummary() {
      if (!this.config || this.config === null) return false;
      if (!this.project || this.project === null) return false;
      return [
        this.config.projectOverviewPage.submitStatus.Approved,
        this.config.projectOverviewPage.submitStatus.Completed,
        this.config.projectOverviewPage.submitStatus.Cancelled,
      ].includes(this.project.approvalStatus);
    },
    projectBudgetIncrease() {
      if (!this.projectBudget || !this.projectBudget.projectBudgetYears) {
        return 0.0;
      }
      return this.projectBudget.projectBudgetYears.flatMap((pby) => pby.annualBudgetIncreases.map(
        (ba) => ba.adjustmentToProjectBudget,
      )).reduce(
        (a, b) => a + b, 0.0,
      );
    },
    projectBudgetDecrease() {
      if (!this.projectBudget || !this.projectBudget.projectBudgetDecreases) {
        return 0.0;
      }
      return this.projectBudget.projectBudgetDecreases.map(
        (ba) => ba.decreaseAmount,
      ).reduce(
        (a, b) => a + b, 0.0,
      );
    },
    projectExpenditureTotalProjectCost() {
      if (!this.projectSnapshot || this.projectSnapshot === null) return 0;

      const costShare = Math.round(this.projectExpenditureCostShare * 100);
      const districtAndPartnerCost = Math.round(this.projectExpenditureDistrictOrPartnerCost * 100);
      const projectSnapshotTotalAmount = costShare + districtAndPartnerCost;

      let totalApproved = projectSnapshotTotalAmount;

      totalApproved /= Math.pow(10, 2);

      return totalApproved;
    },
    projectExpenditureCostShare() {
      if (!this.projectSnapshot || this.projectSnapshot === null) return 0;

      return this.projectSnapshot.costShare_Total_Cost;
    },
    projectExpenditureDistrictOrPartnerCost() {
      let totalDistrictOrPartnerCost = 0;
      const totalDistrictOrPartnerCostItems = this.projectExpenditureDistrictOrPartnerCostItems;

      if (totalDistrictOrPartnerCostItems.length > 0) {
        totalDistrictOrPartnerCost = totalDistrictOrPartnerCostItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalDistrictOrPartnerCost /= Math.pow(10, 2);

      return totalDistrictOrPartnerCost;
    },
    projectExpenditureDistrictOrPartnerCostItems() {
      if (!this.fundingSources || this.fundingSources === null) return [];
      if (!this.projectSnapshotDraft || this.projectSnapshotDraft === null) return [];
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];

      const currentProjectBudgetYears = this.projectSnapshotDraft.projectBudgetYearSnapshots;
      if (!currentProjectBudgetYears || currentProjectBudgetYears === null) return [];

      const annualBudgetIncreases = this.projectBudget.projectBudgetYears.flatMap((pby) => pby.annualBudgetIncreases);
      const projectBudgetDecreases = this.projectBudget.projectBudgetDecreases;

      const currentProjectLineItems = [];
      currentProjectBudgetYears.forEach((by) => {
        by.projectBudgetLineItemSnapshots.forEach((li) => {
          currentProjectLineItems.push(li);
        });
      });

      const usedFundingSourceIds = Array.from(new Set(
        currentProjectLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const annualIncreasesFundingSourceIds = Array.from(new Set(
        annualBudgetIncreases.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const incentiveFundingSourceIds = Array.from(new Set(
        this.projectIncentivePayments.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const fundingSourceIds = [...new Set([...usedFundingSourceIds, ...annualIncreasesFundingSourceIds, ...incentiveFundingSourceIds])];

      const totalSpentByFundingSource = {};
      fundingSourceIds.forEach((e) => {
        totalSpentByFundingSource[e?.toString()] = 0;
      });

      currentProjectLineItems.forEach((e) => {
        const baseCost = e.unitCost * e.quantity * (e.costShare * 0.01);
        const contingencyCost = e.unitCost * e.quantity * (e.costShare * 0.01) * (e.contingencyPercent * 0.01);
        const totalCost = baseCost + contingencyCost;

        totalSpentByFundingSource[e.fundingSourceId?.toString()] += totalCost;
      });

      const filteredFundingSources = this.fundingSources.map((fs) => {
        const recordId = fs.recorD_ID;
        const recordName = fs.recorD_NAME;
        return {
          id: recordId,
          name: recordName,
        };
      });

      const mappedFundingSources = new Map();
      filteredFundingSources.forEach((fs) => {
        mappedFundingSources.set(fs.id?.toString(), fs.name);
      });

      fundingSourceIds.forEach((e) => {
        const incentives = this.projectIncentivePayments
          .filter((incentive) => incentive?.fundingSourceId?.toString() === e?.toString())
          .map((incentive) => incentive.amountPaid);

        let incentiveAmount = 0;
        if (incentives.length > 0) {
          incentiveAmount = incentives.reduce((a, b) => a + b, 0.0);
        }

        totalSpentByFundingSource[e?.toString()] += (Math.round((incentiveAmount * 100)) / Math.pow(10, 2));
      });

      const totalCostItems = fundingSourceIds.map((e) => {
        const itemCost = totalSpentByFundingSource[e?.toString()];

        const increases = annualBudgetIncreases.filter(
          (inc) => inc.fundingSourceId?.toString() === e?.toString(),
        );

        let totalIncrease = 0.0;
        increases.forEach((inc) => {
          totalIncrease += inc.adjustmentToProjectBudget;
        });

        const decreases = projectBudgetDecreases.filter(
          (dec) => dec.fundingSourceId?.toString() === e?.toString(),
        );

        let totalDecrease = 0.0;
        decreases.forEach((dec) => {
          totalDecrease += dec.decreaseAmount;
        });

        return {
          id: e,
          name: mappedFundingSources.get(e?.toString()),
          total: (itemCost + totalIncrease - totalDecrease).toFixed(2),
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalCostItems;
    },
    projectExpenditureTotalObligated() {
      const spent = this.projectExpenditureTotalObligatedSpent;
      const unspent = this.projectExpenditureTotalObligatedUnspent;

      return spent + unspent;
    },
    projectExpenditureTotalObligatedSpent() {
      let totalObligatedSpent = 0;

      const totalObligatedSpentItems = this.projectExpenditureTotalObligatedSpentItems;

      if (totalObligatedSpentItems.length > 0) {
        totalObligatedSpent = totalObligatedSpentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedSpent /= Math.pow(10, 2);

      return totalObligatedSpent;
    },
    projectExpenditureTotalObligatedSpentItems() {
      if (!this.projectWorkorders || this.projectWorkorders === null) return [];
      if (!this.fundingSourceMap) return [];

      const workorders = this.projectWorkorders.filter(
        (e) => e.workOrderBills
          && (e.status === 'Completed'),
      );

      const paidWorkorderBills = workorders.map(
        (e) => e.workOrderBills,
      ).flat().filter(
        (e) => e.status === this.config.workOrderBillForm.submitStatus.Paid,
      );

      const paidWorkorderBillLineItems = paidWorkorderBills.map(
        (e) => e.workOrderBillLineItems,
      ).flat();
      const paidWorkorderBillIncentives = paidWorkorderBills.map(
        (e) => e.workOrderBillIncentivePayments,
      ).flat();

      const lineItemFundingSourceIds = Array.from(new Set(
        paidWorkorderBillLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));
      const incentiveFundingSourceIds = Array.from(new Set(
        paidWorkorderBillIncentives.map(
          (e) => e.fundingSourceId,
        ),
      ));
      const allFundingSourceIds = [...new Set([...lineItemFundingSourceIds, ...incentiveFundingSourceIds])];

      const totalSpentByFundingSource = {};
      allFundingSourceIds.forEach((e) => {
        totalSpentByFundingSource[e?.toString()] = 0;
      });

      paidWorkorderBillLineItems.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.itemCost * 100));
      });
      paidWorkorderBillIncentives.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.amountBilled * 100));
      });

      const totalSpentItems = allFundingSourceIds.map((e) => {
        let itemCost = totalSpentByFundingSource[e?.toString()];
        itemCost /= Math.pow(10, 2);
        return {
          id: e,
          name: this.fundingSourceMap.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalSpentItems;
    },
    projectExpenditureTotalObligatedUnspent() {
      let totalObligatedUnspent = 0;

      const totalObligatedUnspentItems = this.projectExpenditureTotalObligatedUnspentItems;

      if (totalObligatedUnspentItems.length > 0) {
        totalObligatedUnspent = totalObligatedUnspentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedUnspent /= Math.pow(10, 2);

      return totalObligatedUnspent;
    },
    projectExpenditureTotalObligatedUnspentItems() {
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];
      if (!this.projectWorkorders || this.projectWorkorders === null) return [];
      if (!this.fundingSourceMap) return [];
      if (!this.projectWorkorderLineItemFundingSources
        || this.projectWorkorderLineItemFundingSources === null
        || this.projectWorkorderLineItemFundingSources.length === 0) return [];

      const workorders = this.projectWorkorders.filter(
        (e) => e.workOrderBills
          && ((e.status === 'Accepted') || (e.status === 'Draft') || (e.status === 'Review') || (e.status === 'Submitted')),
      );

      const fundingSourceIds = Array.from(new Set(
        this.projectWorkorderLineItemFundingSources.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const workorderIncentives = workorders.map(
        (e) => e.workOrderProjectIncentives,
      ).flat();

      const incentiveFundingSourceIds = Array.from(new Set(
        workorderIncentives.map(
          (e) => this.projectIncentivePayments.find((pip) => pip.id === e.projectIncentivePaymentId).fundingSourceId,
        ),
      ));

      const allFundingSourceIds = [...new Set([...fundingSourceIds, ...incentiveFundingSourceIds])];

      const totalUnspentByFundingSource = {};
      allFundingSourceIds.forEach((e) => {
        totalUnspentByFundingSource[e?.toString()] = 0;
      });

      const workorderLineItems = workorders.map(
        (e) => e.workOrderLineItems,
      ).flat();

      workorderLineItems.forEach((e) => {
        const fsource = this.projectWorkorderLineItemFundingSources.find((fs) => fs.workOrderLineItemId === e.workOrderLineItemId);
        const totalSpent = ((e.quantity * e.salePrice * 100) * (e.districtAndPartnerCost * 0.01));
        totalUnspentByFundingSource[fsource.fundingSourceId?.toString()] += totalSpent;
      });

      workorderIncentives.forEach((e) => {
        const fundingSourceId = this.projectIncentivePayments.find((pip) => pip.id === e.projectIncentivePaymentId).fundingSourceId;
        totalUnspentByFundingSource[fundingSourceId?.toString()] += Math.round((e.paymentAmount * 100));
      });

      const totalObligatedUnspentItems = allFundingSourceIds.map((e) => {
        let itemCost = totalUnspentByFundingSource[e?.toString()];
        itemCost /= Math.pow(10, 2);
        if (itemCost < 0) itemCost = 0;
        const name = this.fundingSourceMap.get(e?.toString());

        return {
          id: e,
          name,
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalObligatedUnspentItems;
    },
    projectExpenditureObligatedSpent() {
      let totalObligatedSpent = 0;

      const totalObligatedSpentItems = this.projectExpenditureObligatedSpentItems;

      if (totalObligatedSpentItems.length > 0) {
        totalObligatedSpent = totalObligatedSpentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedSpent /= Math.pow(10, 2);

      return totalObligatedSpent;
    },
    projectExpenditureObligatedSpentItems() {
      if (!this.projectWorkorders || this.projectWorkorders === null) return [];
      if (!this.fundingSourceMap) return [];

      const workorders = this.projectWorkorders.filter(
        (e) => e.workOrderBills
          && (e.status === 'Completed' || e.status === 'Accepted'),
      );

      const paidWorkorderBills = workorders.map(
        (e) => e.workOrderBills,
      ).flat().filter(
        (e) => e.status === this.config.workOrderBillForm.submitStatus.Paid,
      );

      const paidWorkorderBillLineItems = paidWorkorderBills.map(
        (e) => e.workOrderBillLineItems,
      ).flat();
      const paidWorkorderBillIncentives = paidWorkorderBills.map(
        (e) => e.workOrderBillIncentivePayments,
      ).flat();

      const woBillFundingSourceIds = Array.from(new Set(
        paidWorkorderBillLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));
      const woIncentiveFundingSourceIds = Array.from(new Set(
        paidWorkorderBillIncentives.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const allFundingSourceIds = [...new Set([...woBillFundingSourceIds, ...woIncentiveFundingSourceIds])];

      const totalSpentByFundingSource = {};
      allFundingSourceIds.forEach((e) => {
        totalSpentByFundingSource[e?.toString()] = 0;
      });

      paidWorkorderBillLineItems.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.itemCost * 100));
      });
      paidWorkorderBillIncentives.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.amountBilled * 100));
      });

      const totalSpentItems = allFundingSourceIds.map((e) => {
        let itemCost = totalSpentByFundingSource[e?.toString()];
        itemCost /= Math.pow(10, 2);
        return {
          id: e,
          name: this.fundingSourceMap.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalSpentItems;
    },
    projectExpenditureTotalObligatedItems() {
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];
      if (!this.projectWorkorders || this.projectWorkorders === null) return 0;
      if (!this.fundingSourceMap) return 0;

      const workorders = this.projectWorkorders.filter(
        (e) => ((e.status === 'Completed') || (e.status === 'Accepted')
          || (e.status === 'Draft') || (e.status === 'Review') || (e.status === 'Submitted')),
      );

      const workOrderFundingItems = [];
      workorders.forEach((wo) => {
        let unspentLineItemFunding = [];
        let paidBillFunding = [];
        const acceptedStatusFunding = [];

        if (wo.status === 'Draft' || (wo.status === 'Review') || (wo.status === 'Submitted')) {
          unspentLineItemFunding = this.getUnspentLineItemFunding(wo);
        }
        if (wo.status === 'Completed') {
          paidBillFunding = this.getPaidBillFunding(wo);
        }
        if (wo.status === 'Accepted') {
          const acceptedUnspentLineItemFunding = this.getUnspentLineItemFunding(wo);
          const acceptedPaidBillFunding = this.getPaidBillFunding(wo);
          acceptedUnspentLineItemFunding.forEach((item) => {
            acceptedStatusFunding.push(item);
          });
          acceptedPaidBillFunding.forEach((item) => {
            const liIndex = acceptedStatusFunding.findIndex((lif) => lif.fundingSourceId === item.fundingSourceId);
            if (liIndex >= 0) {
              acceptedStatusFunding[liIndex].total = acceptedStatusFunding[liIndex].total > item.total ? acceptedStatusFunding[liIndex].total : item.total;
            } else {
              acceptedStatusFunding.push({
                fundingSourceId: item.fundingSourceId,
                total: item.total,
              });
            }
          });
        }

        unspentLineItemFunding.forEach((unspent) => {
          workOrderFundingItems.push(unspent);
        });
        paidBillFunding.forEach((paid) => {
          workOrderFundingItems.push(paid);
        });
        acceptedStatusFunding.forEach((accpted) => {
          workOrderFundingItems.push(accpted);
        });
      });

      const totalObligatedItems = [];
      workOrderFundingItems.forEach((item) => {
        const liIndex = totalObligatedItems.findIndex((lif) => lif.id === item.fundingSourceId);
        if (liIndex >= 0) {
          totalObligatedItems[liIndex].total += item.total;
        } else {
          totalObligatedItems.push({
            name: this.fundingSourceMap.get(item.fundingSourceId?.toString()),
            id: item.fundingSourceId,
            total: item.total,
          });
        }
      });

      return totalObligatedItems;
    },
    projectExpenditureTotalRemaining() {
      const items = this.projectExpenditureTotalRemainingItems;

      let total = 0.0;
      Object.keys(items).forEach((e) => {
        total += items[e].total;
      });

      return total;
    },
    projectExpenditureTotalRemainingItems() {
      const districtAndPartnerItems = this.projectExpenditureDistrictOrPartnerCostItems;
      const totalObligatedItems = this.projectExpenditureTotalObligatedItems;

      const distPartnerFundingSourceIds = districtAndPartnerItems.map((item) => (item.id));
      const obligatedFundingSourceIds = totalObligatedItems.map((item) => (item.id));
      const allfundingSourceIds = [...new Set([...distPartnerFundingSourceIds, ...obligatedFundingSourceIds])];

      const remainingItems = allfundingSourceIds.map((fundingSourceId) => {
        let districtAndPartnerItemCost = 0;
        let itemName = '';
        let totalObligatedItemCost = 0;

        const districtAndPartnerItem = districtAndPartnerItems.find((e) => e.id?.toString() === fundingSourceId?.toString());
        if (districtAndPartnerItem) {
          districtAndPartnerItemCost = districtAndPartnerItem.total;
          itemName = districtAndPartnerItem.name;
        }

        const totalObligatedItem = totalObligatedItems.find((e) => e.id?.toString() === fundingSourceId?.toString());
        if (totalObligatedItem) {
          totalObligatedItemCost = totalObligatedItem.total;
          itemName = totalObligatedItem.name;
        }

        return {
          id: fundingSourceId,
          name: itemName,
          total: districtAndPartnerItemCost - totalObligatedItemCost,
        };
      });

      return remainingItems;
    },
    projectExpenditureCancelCompleteTotalRemaining() {
      const items = this.projectExpenditureCancelCompleteTotalRemainingItems;

      let total = 0.0;
      Object.keys(items).forEach((e) => {
        total += items[e].total;
      });

      return total;
    },
    projectExpenditureCancelCompleteTotalRemainingItems() {
      const districtAndPartnerItems = this.projectExpenditureDistrictOrPartnerCostItems;
      const totalObligatedSpentItems = this.projectExpenditureTotalObligatedSpentItems;

      const remainingItems = districtAndPartnerItems.map((item) => {
        const totalCost = item.total;
        let obligated = 0.0;

        const obligatedItem = totalObligatedSpentItems.find((e) => e.id?.toString() === item.id?.toString());
        if (obligatedItem) {
          obligated = obligatedItem.total;
        }

        const totalRemaining = totalCost - obligated;
        return {
          id: item.id,
          name: item.name,
          total: totalRemaining,
        };
      });

      return remainingItems;
    },
  },
  methods: {
    async loadProjectBudgetSummaryData() {
      if (!this.project || this.project === null) {
        await this.$store.dispatch('Project/getProject', parseInt(this.$route.params.id, 10));
      }

      await this.$store.dispatch('Project/getProjectSnapshot', parseInt(this.$route.params.id, 10));
      await this.$store.dispatch('Project/getProjectBudgetYearSnapshots', parseInt(this.$route.params.id, 10));
      await this.$store.dispatch('Project/getProjectWorkorders', parseInt(this.$route.params.id, 10));
      await this.loadProjectWorkorderBills();

      if (this.projectWorkorders) {
        await this.$store.dispatch(
          'Project/getProjectWorkorderLineItemFundingSources',
          this.projectWorkorders.map((e) => e.id),
        );
      }

      const fiscalYear = this.getFiscalYear(this.project.startDate);
      await this.$store.dispatch('Project/getProjectSnapshots', fiscalYear);
    },
    async loadProjectWorkorderBills() {
      await this.$store.dispatch(
        'Project/getProjectWorkorderBills',
        this.projectWorkorders.map((e) => e.id),
      );

      const workOrderBillLineItems = this.projectWorkorderBills.map((e) => e.workOrderBillLineItems).flat();
      workOrderBillLineItems.forEach((e) => {

        const workOrderBill = this.projectWorkorderBills.find((item) => item.id === e.workOrderBillId);
        const workOrder = this.projectWorkorders.find((item) => item.id === workOrderBill.workOrderId);
        const workOrderLineItem = workOrder.workOrderLineItems.find((item) => item.workOrderLineItemId === e.workOrderLineItemId);

        if (workOrderLineItem && workOrderLineItem != null) {
          e.lineItemProductName = workOrderLineItem.productName;
          e.lineItemProductUnit = workOrderLineItem.productUnit;
          e.lineItemQuantity = workOrderLineItem.quantity;
        }

        e.itemCost = e.salePrice * e.quantity;

        const workOrderLineItemIds = workOrder.workOrderLineItems.map((item) => item.workOrderLineItemId);
        const workOrderLineItemFundingSources = this.projectWorkorderLineItemFundingSources.filter((item) => workOrderLineItemIds.includes(item.workOrderLineItemId));
        const fundingSourceIds = workOrderLineItemFundingSources.map((item) => (item.fundingSourceId));

        e.fundingSourceOptions = this.fundingSources.filter(
          (item) => fundingSourceIds.includes(item.recorD_ID),
        ).map(
          (item) => ({ value: item.recorD_ID, text: item.recorD_NAME }),
        ).sort(
          (a, b) => a.text?.localeCompare(b.text),
        );

      });

      this.projectWorkorders.map((e) => e.id).forEach((workorderId) => {
        const workorder = this.projectWorkorders.find((item) => item.id === workorderId);
        workorder.workOrderBills = this.projectWorkorderBills.filter((item) => item.workOrderId === workorderId);
      });
    },
    getWorkOrderObligatedUnspentGrandTotal(workOrder) {
      let workOrderLineItemTotal = 0;
      let workOrderBillTotal = 0;

      const lineItemTotals = (workOrder.workOrderLineItems || []).map(
        (e) => Math.round(e.quantity * (e.salePrice * 100) * (e.districtAndPartnerCost * 0.01)),
      );

      const bills = workOrder.workOrderBills;
      const billLineItems = bills.filter((e) => e.status === 'Paid').map((e) => e.workOrderBillLineItems).flat();

      const billLineItemsTotals = (billLineItems || []).map(
        (e) => Math.round(e.itemCost * 100),
      );

      if (billLineItemsTotals.length > 0) {
        workOrderBillTotal = billLineItemsTotals.reduce((lineItemSum, lineItemValue) => lineItemSum + lineItemValue);
      }
      workOrderBillTotal /= Math.pow(10, 2);

      if (lineItemTotals.length > 0) {
        workOrderLineItemTotal = lineItemTotals.reduce((lineItemSum, lineItemValue) => lineItemSum + lineItemValue);
      }

      workOrderLineItemTotal /= Math.pow(10, 2);
      const unspentTotal = workOrderLineItemTotal - workOrderBillTotal;

      return unspentTotal;
    },
    getWorkOrderGrandTotal(workOrder) {
      let grandTotal = 0;

      const lineItemTotals = (workOrder.workOrderLineItems || []).map(
        (e) => Math.round(e.quantity * (e.salePrice * 100) * (e.districtAndPartnerCost * 0.01)),
      );

      if (lineItemTotals.length > 0) {
        grandTotal = lineItemTotals.reduce((lineItemSum, lineItemValue) => lineItemSum + lineItemValue);
      }
      grandTotal /= Math.pow(10, 2);

      return grandTotal;
    },
    getPreviousFiscalYearsTotalSpent(fiscalYear, fundingSourceId) {
      if (!this.config || this.config === null) return 0;
      if (!this.projectWorkorders || this.projectWorkorders === null) return 0;

      const fiscalYearStart = new Date(`${fiscalYear - 1}`, 6, 1);

      const workorders = this.projectWorkorders.filter((e) => {
        const requestedDate = new Date(e.requestedDate);
        if (requestedDate < fiscalYearStart) {
          return true;
        }
        return false;
      });

      let workorderBills = workorders.filter(
        (e) => e.workOrderBills,
      ).map(
        (e) => e.workOrderBills,
      ).flat();

      workorderBills = workorderBills.filter(
        (e) => e.status === this.config.workOrderBillForm.submitStatus.Paid,
      );

      const workorderBillLineItems = workorderBills.map(
        (e) => e.workOrderBillLineItems,
      ).flat()
        .filter((e) => e.fundingSourceId === fundingSourceId);

      const workorderBillLineItemCosts = workorderBillLineItems.map(
        (e) => e.itemCost,
      );

      let workorderCost = 0;

      if (workorderBillLineItemCosts.length > 0) {
        workorderCost = workorderBillLineItemCosts.reduce((a, b) => a + b, 0.0);
      }

      return workorderCost;
    },
    getFiscalYearTotalApproved(fiscalYear, fundingSourceId) {
      if (!this.projectBudgetYearSnapshots || this.projectBudgetYearSnapshots === null || this.projectBudgetYearSnapshots.length === 0) return 0;
      const fiscalYearSnapshot = this.projectBudgetYearSnapshots
        .filter((e) => e.status === 'Approved')
        .find((e) => e.year === fiscalYear);

      if (!fiscalYearSnapshot || fiscalYearSnapshot === null) return 0;

      const fiscalYearLineItemSnapshots = fiscalYearSnapshot.projectBudgetLineItemSnapshots
        .filter((e) => e.fundingSourceId === fundingSourceId);

      let snapshotTotal = 0;
      fiscalYearLineItemSnapshots.forEach((e) => {
        const baseCost = ((e.quantity * e.unitCost) * (e.costShare * 0.01));
        const contingency = baseCost * e.contingencyPercent * 0.01;
        snapshotTotal += (baseCost + contingency);
      });

      return snapshotTotal
        + fiscalYearSnapshot.projectBudgetYear.annualBudgetIncreases
          .filter((ba) => ba.fundingSourceId === fundingSourceId)
          .map((ba) => ba.adjustmentToAnnualBudget)
          .reduce((a, b) => a + b, 0.0);
    },
    getProjectBudgetSummaryTotalBudget(fiscalYear) {
      if (!this.projectBudgetYearSnapshots || this.projectBudgetYearSnapshots === null) return 0;
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];

      const incentivesInFiscalYear = this.projectIncentivePayments.filter((incentive) => this.getFiscalYear(incentive.dateDue) === fiscalYear);

      let totalIncentive = 0.0;
      incentivesInFiscalYear.forEach((e) => {
        totalIncentive += e.amountPaid;
      });

      const projectBudgetYearSnapshot = this.projectBudgetYearSnapshots.filter((e) => e.status === 'Approved').find((e) => e.year === fiscalYear);

      if (!projectBudgetYearSnapshot || projectBudgetYearSnapshot === null) return 0;

      let totalBudget = Math.round(projectBudgetYearSnapshot.totalAnnualCost * 100);

      if (
        projectBudgetYearSnapshot.projectBudgetYear
        && projectBudgetYearSnapshot.projectBudgetYear !== null
        && projectBudgetYearSnapshot.projectBudgetYear.annualBudgetIncreases
        && projectBudgetYearSnapshot.projectBudgetYear.annualBudgetIncreases !== null
        && Array.isArray(projectBudgetYearSnapshot.projectBudgetYear.annualBudgetIncreases)
        && projectBudgetYearSnapshot.projectBudgetYear.annualBudgetIncreases.length > 0
      ) {
        const annualBudgetIncreases = projectBudgetYearSnapshot.projectBudgetYear.annualBudgetIncreases.map((e) => Math.round(e.adjustmentToAnnualBudget * 100));
        /* eslint-disable-next-line */
        const adjustmentToProjectBudgetTotal = annualBudgetIncreases.reduce((previousValue, nextValue) => {
          return previousValue + nextValue;
        });
        totalBudget += adjustmentToProjectBudgetTotal;
      }
      totalBudget /= Math.pow(10, 2);

      return totalBudget + totalIncentive;
    },
    getTotalCostItems(budgetYear) {
      if (!this.projectBudgetYearSnapshots || this.projectBudgetYearSnapshots === null) return [];

      const currentProjectBudgetYears = this.projectBudgetYearSnapshots.find((by) => by.year === budgetYear.year);
      if (!currentProjectBudgetYears || currentProjectBudgetYears === null) return [];

      const currentProjectLineItems = [];
      currentProjectBudgetYears.projectBudgetLineItemSnapshots.forEach((li) => {
        currentProjectLineItems.push(li);
      });

      const usedFundingSourceIds = Array.from(new Set(
        currentProjectLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const totalDistrictPartnerCostByFundingSource = {};
      usedFundingSourceIds.forEach((e) => {
        totalDistrictPartnerCostByFundingSource[e?.toString()] = 0;
      });

      currentProjectLineItems.forEach((e) => {
        totalDistrictPartnerCostByFundingSource[e.fundingSourceId?.toString()]
        += (((e.unitCost * e.quantity)) + (e.unitCost * e.quantity * (e.contingencyPercent * 0.01))) * (e.costShare * 0.01);
      });

      const filteredFundingSources = this.fundingSources.map((fs) => {
        const recordId = fs.recorD_ID;
        const recordName = fs.recorD_NAME;
        return {
          id: recordId,
          name: recordName,
        };
      });

      const mappedFundingSources = new Map();
      filteredFundingSources.forEach((fs) => {
        mappedFundingSources.set(fs.id?.toString(), fs.name);
      });

      const totalCostItems = usedFundingSourceIds.map((e) => {
        const itemCost = totalDistrictPartnerCostByFundingSource[e?.toString()];
        return {
          id: e,
          name: mappedFundingSources.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalCostItems;
    },
    getAnnualBudgetExpenditureTotalAnnualCost(budgetYear) {
      const costShare = this.getAnnualBudgetExpenditureCostShare(budgetYear);
      const partnerCost = this.getAnnualBudgetExpenditureDistrictAndPartner(budgetYear);
      const totalAnnualCost = costShare + partnerCost;
      return totalAnnualCost;
    },
    getAnnualBudgetExpenditureCostShare(budgetYear) {
      const costShareItems = this.getAnnualBudgetExpenditureCostShareItems(budgetYear);

      let costShareItemsTotal = 0.0;
      costShareItems.forEach((tci) => {
        costShareItemsTotal += tci.total;
      });

      return costShareItemsTotal;
    },
    getAnnualBudgetExpenditureCostShareItems(budgetYear) {
      if (!this.projectBudgetYearSnapshots || this.projectBudgetYearSnapshots === null || this.projectBudgetYearSnapshots.length === 0) return [];

      const currentProjectBudgetYears = this.projectBudgetYearSnapshots.find((by) => by.year === budgetYear.year);
      if (!currentProjectBudgetYears || currentProjectBudgetYears === null) return [];

      const currentProjectLineItems = [];
      currentProjectBudgetYears.projectBudgetLineItemSnapshots.forEach((li) => {
        currentProjectLineItems.push(li);
      });

      const usedFundingSourceIds = Array.from(new Set(
        currentProjectLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const totalCostShareByFundingSource = {};
      usedFundingSourceIds.forEach((e) => {
        totalCostShareByFundingSource[e?.toString()] = 0;
      });

      currentProjectLineItems.forEach((e) => {
        totalCostShareByFundingSource[e.fundingSourceId?.toString()]
        += (((e.unitCost * e.quantity * ((100 - e.costShare) * 0.01))) + (e.unitCost * e.quantity * ((100 - e.costShare) * 0.01) * (e.contingencyPercent * 0.01)));
      });

      const filteredFundingSources = this.fundingSources.map((fs) => {
        const recordId = fs.recorD_ID;
        const recordName = fs.recorD_NAME;
        return {
          id: recordId,
          name: recordName,
        };
      });

      const mappedFundingSources = new Map();
      filteredFundingSources.forEach((fs) => {
        mappedFundingSources.set(fs.id?.toString(), fs.name);
      });

      const costShareItems = usedFundingSourceIds.map((e) => {
        const itemCost = totalCostShareByFundingSource[e?.toString()];
        return {
          id: e,
          name: mappedFundingSources.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return costShareItems;
    },
    getAnnualBudgetExpenditureDistrictAndPartner(budgetYear) {
      const districtAndPartnerItems = this.getAnnualBudgetExpenditureDistrictAndPartnerItems(budgetYear);

      let districtAndPartnerItemsTotal = 0.0;
      districtAndPartnerItems.forEach((item) => {
        districtAndPartnerItemsTotal += item.total;
      });

      return districtAndPartnerItemsTotal;
    },
    getAnnualBudgetExpenditureDistrictAndPartnerItems(budgetYear) {
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];
      if (!this.projectBudgetYearSnapshots || this.projectBudgetYearSnapshots === null) return [];

      const incentivesInFiscalYear = this.projectIncentivePayments.filter((incentive) => this.getFiscalYear(incentive.dateDue) === budgetYear.year);
      const annualBudgetIncreases = this.projectBudget.projectBudgetYears.filter((e) => e.year === budgetYear.year).flatMap((pby) => pby.annualBudgetIncreases);

      const currentProjectBudgetYear = this.projectBudgetYearSnapshots.find((by) => by.year === budgetYear.year);
      if (!currentProjectBudgetYear || currentProjectBudgetYear === null) return [];

      const currentProjectLineItems = [];
      currentProjectBudgetYear.projectBudgetLineItemSnapshots.forEach((li) => {
        currentProjectLineItems.push(li);
      });

      const usedFundingSourceIds = Array.from(new Set(
        currentProjectLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const annualIncreasesFundingSourceIds = Array.from(new Set(
        annualBudgetIncreases.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const incentiveFundingSourceIds = Array.from(new Set(
        incentivesInFiscalYear.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const allfundingSourceIds = [...new Set([...usedFundingSourceIds, ...annualIncreasesFundingSourceIds, ...incentiveFundingSourceIds])];

      const totalDistrictPartnerCostByFundingSource = {};
      allfundingSourceIds.forEach((e) => {
        totalDistrictPartnerCostByFundingSource[e?.toString()] = 0;
      });

      currentProjectLineItems.forEach((e) => {
        totalDistrictPartnerCostByFundingSource[e.fundingSourceId?.toString()]
        += Number((((e.unitCost * e.quantity * ((e.costShare) * 0.01))) + (e.unitCost * e.quantity * ((e.costShare) * 0.01) * (e.contingencyPercent * 0.01))).toFixed(2));
      });

      const filteredFundingSources = this.fundingSources.map((fs) => {
        const recordId = fs.recorD_ID;
        const recordName = fs.recorD_NAME;
        return {
          id: recordId,
          name: recordName,
        };
      });

      const mappedFundingSources = new Map();
      filteredFundingSources.forEach((fs) => {
        mappedFundingSources.set(fs.id?.toString(), fs.name);
      });

      allfundingSourceIds.forEach((e) => {
        const incentives = incentivesInFiscalYear
          .filter((incentive) => incentive.fundingSourceId?.toString() === e?.toString())
          .map((incentive) => incentive.amountPaid);

        let incentiveAmount = 0;
        if (incentives.length > 0) {
          incentiveAmount = incentives.reduce((a, b) => a + b, 0.0);
        }

        totalDistrictPartnerCostByFundingSource[e?.toString()] += Math.round((incentiveAmount * 100) / Math.pow(10, 2));
      });

      const districtAndPartnerItems = allfundingSourceIds.map((e) => {
        const increases = annualBudgetIncreases.filter(
          (inc) => inc.fundingSourceId?.toString() === e?.toString(),
        );

        let totalIncrease = 0.0;
        increases.forEach((inc) => {
          totalIncrease += inc.adjustmentToAnnualBudget;
        });

        const itemCost = totalDistrictPartnerCostByFundingSource[e?.toString()] + totalIncrease;
        return {
          id: e,
          name: mappedFundingSources.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return districtAndPartnerItems;
    },
    getAnnualBudgetExpenditureTotalObligatedSpent(fiscalYear) {
      let totalObligatedSpent = 0;
      const totalObligatedSpentItems = this.getAnnualBudgetExpenditureTotalObligatedSpentItems(fiscalYear);

      if (totalObligatedSpentItems.length > 0) {
        totalObligatedSpent = totalObligatedSpentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedSpent /= Math.pow(10, 2);

      return totalObligatedSpent;
    },
    getAnnualBudgetExpenditureTotalObligatedSpentItems(fiscalYear) {
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];
      if (!this.projectWorkorders || this.projectWorkorders === null) return 0;
      if (!this.fundingSourceMap) return 0;

      const fiscalYearStart = new Date(`${fiscalYear - 1}`, 6, 1);
      const fiscalYearEnd = new Date(`${fiscalYear}`, 6, 1);

      const workorders = this.projectWorkorders.filter(
        (e) => e.workOrderBills
          && (e.status === 'Completed')
          && new Date(e.requestedDate) >= fiscalYearStart
          && new Date(e.requestedDate) < fiscalYearEnd,
      );

      const paidWorkorderBills = workorders.map(
        (e) => e.workOrderBills,
      ).flat().filter(
        (e) => e.status === this.config.workOrderBillForm.submitStatus.Paid,
      );

      const paidWorkorderBillLineItems = paidWorkorderBills.map(
        (e) => e.workOrderBillLineItems,
      ).flat();
      const paidWorkorderBillIncentives = paidWorkorderBills.map(
        (e) => e.workOrderBillIncentivePayments,
      ).flat();

      const lineItemFundingSourceIds = Array.from(new Set(
        paidWorkorderBillLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));
      const incentiveFundingSourceIds = Array.from(new Set(
        paidWorkorderBillIncentives.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const totalSpentByFundingSource = {};

      const allFundingSourceIds = [...new Set([...lineItemFundingSourceIds, ...incentiveFundingSourceIds])];
      allFundingSourceIds.forEach((e) => {
        totalSpentByFundingSource[e?.toString()] = 0;
      });

      paidWorkorderBillLineItems.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.itemCost * 100));
      });
      paidWorkorderBillIncentives.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.amountBilled * 100));
      });

      const totalSpentItems = allFundingSourceIds.map((e) => {
        let itemCost = totalSpentByFundingSource[e?.toString()];
        itemCost /= Math.pow(10, 2);
        return {
          id: e,
          name: this.fundingSourceMap.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalSpentItems;
    },
    getAnnualBudgetExpenditureTotalObligatedUnspent(fiscalYear) {
      let totalObligatedUnspent = 0;
      const totalObligatedUnspentItems = this.getAnnualBudgetExpenditureTotalObligatedUnspentItems(fiscalYear);

      if (totalObligatedUnspentItems.length > 0) {
        totalObligatedUnspent = totalObligatedUnspentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedUnspent /= Math.pow(10, 2);

      return totalObligatedUnspent;
    },
    getAnnualBudgetExpenditureTotalObligatedUnspentItems(fiscalYear) {
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];
      if (!this.projectWorkorders || this.projectWorkorders === null) return [];
      if (!this.fundingSourceMap) return 0;
      if (!this.projectWorkorderLineItemFundingSources
        || this.projectWorkorderLineItemFundingSources === null
        || this.projectWorkorderLineItemFundingSources.length === 0) return [];

      const fiscalYearStart = new Date(`${fiscalYear - 1}`, 6, 1);
      const fiscalYearEnd = new Date(`${fiscalYear}`, 6, 1);

      const workorders = this.projectWorkorders.filter(
        (e) => e.workOrderBills
          && ((e.status === 'Accepted') || (e.status === 'Draft') || (e.status === 'Review') || (e.status === 'Submitted'))
          && new Date(e.requestedDate) >= fiscalYearStart
          && new Date(e.requestedDate) < fiscalYearEnd,
      );

      const workorderLineItems = workorders.map(
        (e) => e.workOrderLineItems,
      ).flat();
      const workorderIncentives = workorders.map(
        (e) => e.workOrderProjectIncentives,
      ).flat();

      const lineItemFundingSourceIds = Array.from(new Set(
        workorderLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));
      const incentiveFundingSourceIds = Array.from(new Set(
        workorderIncentives.map(
          (e) => {
            const fId = this.projectIncentivePayments.find((pi) => pi.id === e.projectIncentivePaymentId).fundingSourceId;
            return fId;
          },
        ),
      ));

      const allFundingSourceIds = [...new Set([...lineItemFundingSourceIds, ...incentiveFundingSourceIds])];
      const totalUnspentByFundingSource = {};
      allFundingSourceIds.forEach((e) => {
        totalUnspentByFundingSource[e?.toString()] = 0;
      });

      workorderLineItems.forEach((e) => {
        const fsource = this.projectWorkorderLineItemFundingSources.find((fs) => fs.workOrderLineItemId === e.workOrderLineItemId);
        const totalSpent = ((e.quantity * e.salePrice * 100) * (e.districtAndPartnerCost * 0.01));
        totalUnspentByFundingSource[fsource.fundingSourceId?.toString()] += totalSpent;
      });
      workorderIncentives.forEach((e) => {
        const fId = this.projectIncentivePayments.find((pi) => pi.id === e.projectIncentivePaymentId).fundingSourceId;
        totalUnspentByFundingSource[fId?.toString()] += (e.paymentAmount * 100);
      });

      const totalObligatedUnspentItems = allFundingSourceIds.map((e) => {
        let itemCost = totalUnspentByFundingSource[e?.toString()];
        itemCost /= Math.pow(10, 2);
        if (itemCost < 0) itemCost = 0;
        const name = this.fundingSourceMap.get(e?.toString());

        return {
          id: e,
          name,
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalObligatedUnspentItems;
    },
    getAnnualBudgetExpenditureObligatedSpent(fiscalYear) {
      let totalObligatedSpent = 0;
      const totalObligatedSpentItems = this.getAnnualBudgetExpenditureObligatedSpentItems(fiscalYear);

      if (totalObligatedSpentItems.length > 0) {
        totalObligatedSpent = totalObligatedSpentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedSpent /= Math.pow(10, 2);

      return totalObligatedSpent;
    },
    getAnnualBudgetExpenditureObligatedSpentItems(fiscalYear) {
      if (!this.projectWorkorders || this.projectWorkorders === null) return 0;
      if (!this.fundingSourceMap) return 0;

      const fiscalYearStart = new Date(`${fiscalYear - 1}`, 6, 1);
      const fiscalYearEnd = new Date(`${fiscalYear}`, 6, 1);

      const workorders = this.projectWorkorders.filter(
        (e) => e.workOrderBills
          && (e.status === 'Completed' || e.status === 'Accepted')
          && new Date(e.requestedDate) >= fiscalYearStart
          && new Date(e.requestedDate) < fiscalYearEnd,
      );

      const paidWorkorderBills = workorders.map(
        (e) => e.workOrderBills,
      ).flat().filter(
        (e) => e.status === this.config.workOrderBillForm.submitStatus.Paid,
      );

      const paidWorkorderBillLineItems = paidWorkorderBills.map(
        (e) => e.workOrderBillLineItems,
      ).flat();
      const paidWorkorderBillIncentives = paidWorkorderBills.map(
        (e) => e.workOrderBillIncentivePayments,
      ).flat();

      const lineItemFundingSourceIds = Array.from(new Set(
        paidWorkorderBillLineItems.map(
          (e) => e.fundingSourceId,
        ),
      ));
      const incentiveFundingSourceIds = Array.from(new Set(
        paidWorkorderBillIncentives.map(
          (e) => e.fundingSourceId,
        ),
      ));

      const totalSpentByFundingSource = {};
      const allFundingSourceIds = [...new Set([...lineItemFundingSourceIds, ...incentiveFundingSourceIds])];
      allFundingSourceIds.forEach((e) => {
        totalSpentByFundingSource[e?.toString()] = 0;
      });

      paidWorkorderBillLineItems.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.itemCost * 100));
      });
      paidWorkorderBillIncentives.forEach((e) => {
        totalSpentByFundingSource[e.fundingSourceId?.toString()] += Math.round((e.amountBilled * 100));
      });

      const totalSpentItems = allFundingSourceIds.map((e) => {
        let itemCost = totalSpentByFundingSource[e?.toString()];
        itemCost /= Math.pow(10, 2);
        return {
          id: e,
          name: this.fundingSourceMap.get(e?.toString()),
          total: itemCost,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return totalSpentItems;
    },
    getAnnualBudgetExpenditureObligatedUnspent(fiscalYear) {
      const totalObligated = this.getAnnualBudgetExpenditureTotalObligated(fiscalYear);
      const obligatedSpent = this.getAnnualBudgetExpenditureObligatedSpent(fiscalYear);
      const totalObligatedUnspent = totalObligated - obligatedSpent;

      return totalObligatedUnspent;
    },
    getAnnualBudgetExpenditureObligatedUnspentItems(fiscalYear) {
      const totalObligatedItems = this.getAnnualBudgetExpenditureTotalObligatedItems(fiscalYear);
      const obligatedSpentItems = this.getAnnualBudgetExpenditureObligatedSpentItems(fiscalYear);

      const obligatedFundingSourceIds = Array.from(new Set(
        totalObligatedItems.map(
          (e) => e.id,
        ),
      ));
      const spentFundingSourceIds = Array.from(new Set(
        obligatedSpentItems.map(
          (e) => e.id,
        ),
      ));

      const concatFundingSourceIds = obligatedFundingSourceIds.concat(spentFundingSourceIds);
      const allFundingSourceIds = concatFundingSourceIds.filter((item, idx) => concatFundingSourceIds.indexOf(item) === idx);

      const obligatedUnspentItems = allFundingSourceIds.map((e) => {
        let obligated = 0;
        let spent = 0;
        let name = '';

        const obligatedItem = totalObligatedItems.find((f) => f?.id?.toString() === e?.toString());
        if (obligatedItem) {
          obligated += obligatedItem.total;
          name = obligatedItem.name;
        }

        const spentItem = obligatedSpentItems.find((f) => f?.id?.toString() === e?.toString());
        if (spentItem) {
          spent += spentItem.total;
          name = spentItem.name;
        }

        const totalUnspent = obligated - spent;

        return {
          id: e,
          name,
          total: totalUnspent,
          // total: totalUnspent < 0 ? 0 : totalUnspent,
        };
      }).sort((a, b) => a.name?.localeCompare(b.name));

      return obligatedUnspentItems;
    },
    getAnnualBudgetExpenditureTotalRemaining(budgetYear) {
      const items = this.getAnnualBudgetExpenditureTotalRemainingItems(budgetYear);

      let total = 0.0;
      Object.keys(items).forEach((e) => {
        total += items[e].total;
      });

      return total;
    },
    getAnnualBudgetExpenditureTotalRemainingItems(budgetYear) {
      const districtAndPartnerItems = this.getAnnualBudgetExpenditureDistrictAndPartnerItems(budgetYear);
      const totalObligatedItems = this.getAnnualBudgetExpenditureTotalObligatedItems(budgetYear.year);
      const distPartnerFundingSourceIds = districtAndPartnerItems.map((item) => (item.id));
      const obligatedFundingSourceIds = totalObligatedItems.map((item) => (item.id));
      const allfundingSourceIds = [...new Set([...distPartnerFundingSourceIds, ...obligatedFundingSourceIds])];

      const remainingItems = allfundingSourceIds.map((fundingSourceId) => {
        let districtAndPartnerItemCost = 0;
        let itemName = '';
        let totalObligatedItemCost = 0;

        const districtAndPartnerItem = districtAndPartnerItems.find((e) => e?.id?.toString() === fundingSourceId?.toString());
        if (districtAndPartnerItem) {
          districtAndPartnerItemCost = districtAndPartnerItem.total;
          itemName = districtAndPartnerItem.name;
        }

        const totalObligatedItem = totalObligatedItems.find((e) => e?.id?.toString() === fundingSourceId?.toString());
        if (totalObligatedItem) {
          totalObligatedItemCost = totalObligatedItem.total;
          itemName = totalObligatedItem.name;
        }

        return {
          id: fundingSourceId,
          name: itemName,
          total: districtAndPartnerItemCost - totalObligatedItemCost,
        };
      });

      return remainingItems;
    },
    getAnnualBudgetExpenditureTotalObligated(fiscalYear) {
      let totalObligatedSpent = 0;
      const totalObligatedSpentItems = this.getAnnualBudgetExpenditureTotalObligatedItems(fiscalYear);
      if (totalObligatedSpentItems.length > 0) {
        totalObligatedSpent = totalObligatedSpentItems.map(
          (e) => Math.round(e.total * 100),
        ).reduce(
          (a, b) => a + b,
        );
      }
      totalObligatedSpent /= Math.pow(10, 2);
      return totalObligatedSpent;
    },
    getAnnualBudgetExpenditureTotalObligatedItems(fiscalYear) {
      if (!this.projectIncentivePayments || this.projectIncentivePayments === null) return [];
      if (!this.projectWorkorders || this.projectWorkorders === null) return 0;
      if (!this.fundingSourceMap) return 0;

      const fiscalYearStart = new Date(`${fiscalYear - 1}`, 6, 1);
      const fiscalYearEnd = new Date(`${fiscalYear}`, 6, 1);
      const workorders = this.projectWorkorders.filter(
        (e) => ((e.status === 'Completed') || (e.status === 'Accepted')
          || (e.status === 'Draft') || (e.status === 'Review') || (e.status === 'Submitted'))
          && new Date(e.requestedDate) >= fiscalYearStart
          && new Date(e.requestedDate) < fiscalYearEnd,
      );

      const workOrderFundingItems = [];
      workorders.forEach((wo) => {
        let unspentLineItemFunding = [];
        let paidBillFunding = [];
        const acceptedStatusFunding = [];

        if (wo.status === 'Draft' || (wo.status === 'Review') || (wo.status === 'Submitted')) {
          unspentLineItemFunding = this.getUnspentLineItemFunding(wo);
        }
        if (wo.status === 'Completed') {
          paidBillFunding = this.getPaidBillFunding(wo);
        }
        if (wo.status === 'Accepted') {
          const acceptedUnspentLineItemFunding = this.getUnspentLineItemFunding(wo);
          const acceptedPaidBillFunding = this.getPaidBillFunding(wo);
          acceptedUnspentLineItemFunding.forEach((item) => {
            acceptedStatusFunding.push(item);
          });
          acceptedPaidBillFunding.forEach((item) => {
            const liIndex = acceptedStatusFunding.findIndex((lif) => lif.fundingSourceId === item.fundingSourceId);
            if (liIndex >= 0) {
              acceptedStatusFunding[liIndex].total = acceptedStatusFunding[liIndex].total > item.total ? acceptedStatusFunding[liIndex].total : item.total;
            } else {
              acceptedStatusFunding.push({
                fundingSourceId: item.fundingSourceId,
                total: item.total,
              });
            }
          });
        }

        unspentLineItemFunding.forEach((unspent) => {
          workOrderFundingItems.push(unspent);
        });
        paidBillFunding.forEach((paid) => {
          workOrderFundingItems.push(paid);
        });
        acceptedStatusFunding.forEach((accpted) => {
          workOrderFundingItems.push(accpted);
        });
      });

      const totalObligatedItems = [];
      workOrderFundingItems.forEach((item) => {
        const liIndex = totalObligatedItems.findIndex((lif) => lif.id === item.fundingSourceId);
        if (liIndex >= 0) {
          totalObligatedItems[liIndex].total += item.total;
        } else {
          totalObligatedItems.push({
            name: this?.fundingSourceMap.get(item?.fundingSourceId?.toString()),
            id: item.fundingSourceId,
            total: item.total,
          });
        }
      });

      return totalObligatedItems;
    },
    getUnspentLineItemFunding(wo) {
      const woLineItems = wo.workOrderLineItems;
      const woIncentives = wo.workOrderProjectIncentives;
      const unspentLineItemFunding = [];

      woLineItems.forEach((li) => {
        const liIndex = unspentLineItemFunding.findIndex((lif) => lif.fundingSourceId === li.fundingSourceId);
        if (liIndex >= 0) {
          unspentLineItemFunding[liIndex].total += ((li.quantity * li.salePrice) * (li.districtAndPartnerCost * 0.01));
        } else {
          unspentLineItemFunding.push({
            fundingSourceId: li?.fundingSourceId,
            total: ((li.quantity * li.salePrice) * (li.districtAndPartnerCost * 0.01)),
          });
        }
      });
      woIncentives.forEach((li) => {
        const projIncentive = this.projectIncentivePayments.find((pip) => pip.id === li.projectIncentivePaymentId);

        const liIndex = unspentLineItemFunding.findIndex((lif) => lif.fundingSourceId === li.fundingSourceId);
        if (liIndex >= 0) {
          unspentLineItemFunding[liIndex].total += li.paymentAmount;
        } else {
          unspentLineItemFunding.push({
            fundingSourceId: projIncentive?.fundingSourceId,
            total: li.paymentAmount,
          });
        }
      });
      return unspentLineItemFunding;
    },
    getPaidBillFunding(wo) {
      if (wo.workOrderBills === undefined) return [];

      const paidWorkorderBills = wo.workOrderBills.filter(
        (e) => e.status === this.config.workOrderBillForm.submitStatus.Paid,
      );
      const paidBillFunding = [];

      paidWorkorderBills.forEach((bill) => {
        bill.workOrderBillLineItems.forEach((item) => {
          const liIndex = paidBillFunding.findIndex((lif) => lif.fundingSourceId === item.fundingSourceId);
          if (liIndex >= 0) {
            paidBillFunding[liIndex].total += item.itemCost;
          } else {
            paidBillFunding.push({
              fundingSourceId: item.fundingSourceId,
              total: item.itemCost,
            });
          }
        });
        bill.workOrderBillIncentivePayments.forEach((item) => {
          const liIndex = paidBillFunding.findIndex((lif) => lif.fundingSourceId === item.fundingSourceId);
          if (liIndex >= 0) {
            paidBillFunding[liIndex].total += item.amountBilled;
          } else {
            paidBillFunding.push({
              fundingSourceId: item.fundingSourceId,
              total: item.amountBilled,
            });
          }
        });
      });
      return paidBillFunding;
    },
  },
};
